import { Phone, Mail, MapPin, Clock, MessageCircle, Instagram, Facebook } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import QuickAppointment from "@/components/QuickAppointment";
import { SITE, waHref } from "@/data/site";

export default function Contact() {
  return (
    <>
      <SEO
        title="İletişim"
        description="MediEste Estetik & Güzellik iletişim bilgileri. Bağlıca / Etimesgut, Ankara. WhatsApp, telefon, e-posta ve konum bilgileri."
        path="/iletisim"
      />
      <section className="pt-32 pb-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumb items={[{ label: "İletişim" }]} />
      </section>

      <section className="pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="chip mb-4">İletişim</p>
        <h1 className="font-display text-4xl md:text-6xl text-[#0B1B33] leading-[1.05] max-w-3xl mb-6 tracking-tight">Size Yardımcı Olmaktan Mutluluk Duyarız</h1>
        <p className="text-[#4B5A6E] max-w-2xl leading-relaxed">
          Randevu, danışma ve tüm sorularınız için WhatsApp'tan bize yazın; en kısa sürede dönüş sağlayalım.
        </p>
      </section>

      <section className="pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-10">
          <div className="space-y-4">
            <a href={`tel:${SITE.phoneRaw}`} data-testid="contact-phone" className="flex items-start gap-5 p-6 bg-white border border-[#E5EAF0] rounded-2xl hover:border-[#10B981]/40 hover:shadow-lg transition-all">
              <div className="h-12 w-12 rounded-xl bg-[#10B981]/10 text-[#10B981] flex items-center justify-center shrink-0"><Phone size={20} /></div>
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#7C8CA0] mb-1 font-semibold">Telefon</p>
                <p className="font-display text-xl text-[#0B1B33] font-medium">{SITE.phone}</p>
              </div>
            </a>
            <a href={waHref()} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="flex items-start gap-5 p-6 bg-[#10B981] rounded-2xl hover:brightness-110 transition-all">
              <div className="h-12 w-12 rounded-xl bg-white/20 text-white flex items-center justify-center shrink-0"><MessageCircle size={20} /></div>
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase text-white/80 mb-1 font-semibold">WhatsApp</p>
                <p className="font-display text-xl text-white font-medium">Hemen Yaz</p>
                <p className="text-sm text-white/80 mt-1">Hızlı yanıt için önerimiz</p>
              </div>
            </a>
            <a href={`mailto:${SITE.email}`} data-testid="contact-email" className="flex items-start gap-5 p-6 bg-white border border-[#E5EAF0] rounded-2xl hover:border-[#10B981]/40 hover:shadow-lg transition-all">
              <div className="h-12 w-12 rounded-xl bg-[#10B981]/10 text-[#10B981] flex items-center justify-center shrink-0"><Mail size={20} /></div>
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#7C8CA0] mb-1 font-semibold">E-Posta</p>
                <p className="font-display text-xl text-[#0B1B33] font-medium">{SITE.email}</p>
              </div>
            </a>
            <div className="flex items-start gap-5 p-6 bg-white border border-[#E5EAF0] rounded-2xl">
              <div className="h-12 w-12 rounded-xl bg-[#10B981]/10 text-[#10B981] flex items-center justify-center shrink-0"><MapPin size={20} /></div>
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#7C8CA0] mb-1 font-semibold">Adres</p>
                <p className="font-display text-lg text-[#0B1B33] leading-snug font-medium">{SITE.address}</p>
                <a href={SITE.mapUrl} target="_blank" rel="noreferrer" className="inline-block mt-3 text-[#10B981] text-xs tracking-[0.15em] uppercase font-semibold uline-link">Google Haritalar →</a>
              </div>
            </div>
            <div className="flex items-start gap-5 p-6 bg-white border border-[#E5EAF0] rounded-2xl">
              <div className="h-12 w-12 rounded-xl bg-[#10B981]/10 text-[#10B981] flex items-center justify-center shrink-0"><Clock size={20} /></div>
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#7C8CA0] mb-1 font-semibold">Çalışma Saatleri</p>
                <p className="font-display text-lg text-[#0B1B33] leading-snug font-medium">{SITE.hours}</p>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <a href={SITE.socials.instagram} target="_blank" rel="noreferrer" className="h-12 w-12 rounded-full border border-[#D1D9E4] flex items-center justify-center text-[#0E2B4F] hover:text-white hover:bg-[#10B981] hover:border-[#10B981] transition"><Instagram size={18} /></a>
              <a href={SITE.socials.facebook} target="_blank" rel="noreferrer" className="h-12 w-12 rounded-full border border-[#D1D9E4] flex items-center justify-center text-[#0E2B4F] hover:text-white hover:bg-[#10B981] hover:border-[#10B981] transition"><Facebook size={18} /></a>
            </div>
          </div>

          <div className="min-h-[500px] md:min-h-[600px] bg-[#F5F7FA] border border-[#E5EAF0] overflow-hidden rounded-2xl">
            <iframe
              title="MediEste Konum"
              src="https://www.google.com/maps?q=Ba%C4%9Fl%C4%B1ca%20Bulvar%C4%B1%20Mert%20Caddesi%20No%3A4%2F2%20Etimesgut%20Ankara&output=embed"
              className="w-full h-full min-h-[500px] md:min-h-[600px]"
              loading="lazy"
              style={{ border: 0 }}
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      <section className="pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-5 gap-8 md:gap-10 items-start">
          <div className="md:col-span-2">
            <p className="chip-green mb-4">Online Randevu</p>
            <h2 className="font-display text-3xl md:text-4xl text-[#0B1B33] leading-tight tracking-tight mb-4">
              WhatsApp'a atlamadan buradan da randevu talebi bırakabilirsiniz.
            </h2>
            <p className="text-[#4B5A6E] leading-relaxed text-sm md:text-base">
              Ad, soyad, telefon ve tercih ettiğiniz hizmeti yazın — çalışma saatlerimiz içinde sizi arayarak randevunuzu netleştirelim.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-[#4B5A6E]">
              <li className="flex gap-2"><span className="text-[#4F8B2A]">•</span>Talepler ekibimize anında iletilir.</li>
              <li className="flex gap-2"><span className="text-[#4F8B2A]">•</span>Bilgileriniz yalnızca randevu için kullanılır.</li>
              <li className="flex gap-2"><span className="text-[#4F8B2A]">•</span>{SITE.hours}</li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <QuickAppointment />
          </div>
        </div>
      </section>
    </>
  );
}
